import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { MIN_TRANSCRIPT_SEARCH_QUERY_LENGTH } from '@orn/global/assistant/transcriptSearch.js';
import Modal from '@orn/shared-web/components/Modal/Modal';
import useDebouncedValue from '@orn/shared-web/hooks/useDebouncedValue';
import { SmallSearchIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatInstantInTimeZone, { PACIFIC_TIME_ZONE } from '@orn/shared-web/utils/datetime/formatInstantInTimeZone';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import ellipsizeEmail from '@orn/shared-web/utils/person/ellipsizeEmail';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import AdminSelectUser, {
  type IAdminSelectUserSelection,
  useSeedSelectionFromLink,
} from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Input from '../../components/Input';
import InspectLink from '../../components/InspectLink';
import InspectLinkedText from '../../components/InspectLinkedText';
import { useToast } from '../../components/Toast';
import { nextThreadSearchParams, parseThreadJump, readLinkedUserId } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';

import { useAssistantUserSelection } from './AssistantUserSelectionContext';
import { firstLineOfSummary } from './compactionSummaryPreview';
import { prefixReason } from './prefixReason';
import { formatTokens } from './promptBudgetView';
import ThreadChannelFilterBar, { useThreadChannelFilter } from './ThreadChannelFilterBar';
import {
  buildAlertNotificationIndex,
  formatCostUsd,
  groupMessagesByTurn,
  isResumeInterruptedTurn,
  type FeedbackRow,
} from './transcript/transcriptModel';
import { ResumeInterstitial, TurnGroup } from './transcript/TranscriptView';

import type {
  AdminDeleteAllAssistantThreadsForUserMutation,
  AdminDeleteAllAssistantThreadsForUserMutationVariables,
  AdminDeleteAssistantThreadMutation,
  AdminDeleteAssistantThreadMutationVariables,
  AdminGetAssistantThreadDetailQuery,
  AdminGetAssistantThreadDetailQueryVariables,
  AdminListAssistantFeedbackForThreadQuery,
  AdminListAssistantFeedbackForThreadQueryVariables,
  AdminListRecentAssistantThreadsQuery,
  AdminListRecentAssistantThreadsQueryVariables,
  AdminRecompactAssistantThreadMutation,
  AdminRecompactAssistantThreadMutationVariables,
  AdminReExtractAssistantThreadMemoryMutation,
  AdminReExtractAssistantThreadMemoryMutationVariables,
} from './__generated__/AssistantThreadsScene.gql';

const ADMIN_LIST_THREADS = gql`
  query AdminListRecentAssistantThreads($userId: ID, $query: String) {
    adminListRecentAssistantThreads(userId: $userId, query: $query) {
      hasMore
      threads {
        id
        userId
        userEmail
        familyId
        channel
        latestDirective
        externalThreadKey
        status
        priorThreadId
        lastActivityAt
        createdAt
        updatedAt
        messageCount
        searchMatch {
          matchCount
          turnId
        }
      }
    }
  }
`;

const ADMIN_GET_THREAD_DETAIL = gql`
  query AdminGetAssistantThreadDetail($threadId: ID!, $afterSequence: Int) {
    adminGetAssistantThreadDetail(threadId: $threadId, afterSequence: $afterSequence) {
      hasMore
      nextAfterSequence
      thread {
        id
        userId
        userEmail
        familyId
        channel
        externalThreadKey
        status
        priorThreadId
        lastActivityAt
        createdAt
        updatedAt
        messageCount
        summary
        compactedAt
        compactionModelId
        compactionMetadata {
          keptCount
          summarizedCount
          droppedCount
          inputTokens
          outputTokens
          errors
        }
        memoryExtractedAt
        memoryExtractionModelId
        memoryExtractionMetadata {
          extractedCount
          userCount
          familyCount
          prefCount
          inputTokens
          outputTokens
          updatedCount
          forgottenCount
          staleSkipCount
          duplicateSkipCount
          existingMemoryCount
          existingTruncated
          capExceeded
          errors
        }
        usage {
          roundCount
          inputTokens
          outputTokens
          cacheReadTokens
          cacheWriteTokens
          reasoningTokens
          totalTokens
          costUsd
        }
      }
      messages {
        id
        threadId
        turnId
        sequence
        role
        payload
        modelId
        errorMessage
        startedAt
        completedAt
        latencyMs
        thinkingMs
        firstTextAt
        ttftMs
        createdAt
      }
    }
  }
`;

const ADMIN_DELETE_THREAD = gql`
  mutation AdminDeleteAssistantThread($threadId: ID!, $reason: String!) {
    adminDeleteAssistantThread(threadId: $threadId, reason: $reason)
  }
`;

const ADMIN_DELETE_ALL_THREADS = gql`
  mutation AdminDeleteAllAssistantThreadsForUser($userId: ID!, $reason: String!) {
    adminDeleteAllAssistantThreadsForUser(userId: $userId, reason: $reason)
  }
`;

const ADMIN_RECOMPACT_THREAD = gql`
  mutation AdminRecompactAssistantThread($threadId: ID!) {
    adminRecompactAssistantThread(threadId: $threadId)
  }
`;

const ADMIN_REEXTRACT_THREAD = gql`
  mutation AdminReExtractAssistantThreadMemory($threadId: ID!) {
    adminReExtractAssistantThreadMemory(threadId: $threadId)
  }
`;

const ADMIN_LIST_FEEDBACK_FOR_THREAD = gql`
  query AdminListAssistantFeedbackForThread($threadId: ID!) {
    adminListAssistantFeedbackForThread(threadId: $threadId) {
      id
      userId
      threadId
      turnId
      body
      submitterEmail
      createdAt
    }
  }
`;

type ThreadRow = AdminListRecentAssistantThreadsQuery['adminListRecentAssistantThreads']['threads'][number];

// Stable empty reference so turns without feedback don't get a fresh array each
// render (keeps TurnGroup's props referentially stable).
const EMPTY_FEEDBACK: FeedbackRow[] = [];

const CHANNEL_BADGE: Record<string, string> = {
  app: 'border-border-primary text-fg-secondary',
  sms: 'border-border-brand text-fg-brand-primary',
  email: 'border-fg-warning-primary text-fg-warning-primary',
  headless: 'border-border-success text-fg-success-secondary',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'border-border-primary text-fg-primary',
  archived: 'border-border-primary text-fg-tertiary',
};

/** Typing pause before a transcript search reaches the server. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Owner-email budget in a rail row, in characters. Sized to leave the relative
 * timestamp and the short thread id legible at the rail's width; CSS truncation
 * is the backstop for a proportional-width outlier.
 */
const RAIL_EMAIL_MAX_CHARS = 24;

/**
 * Threads sub-tab of the Assistant tool. The rail lists assistant threads
 * (active + archived) ordered by last-activity desc — every user's by default,
 * one user's once the operator picks one, and only matching threads while a
 * transcript search is active. Click a thread on the left to see its messages
 * on the right, each rendered as a JSON-style card colored by role, with user /
 * assistant prose bolded and brought to the front. Per-row delete +
 * bulk-delete-all-threads are gated behind typed-reason modals; deletes are
 * hard via FK cascade (no soft-delete column).
 */
const AssistantThreadsScene = () => {
  // Deep-link params. Read once for the initial state; `highlightTurnId` is
  // also read every render to badge + scroll the targeted turn.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkThreadId = searchParams.get('threadId');
  const highlightTurnId = searchParams.get('turnId');

  // Selection is shared with the Memory tab so it survives tab switches. A
  // deep link lands on a fresh Assistant shell whose shared selection starts
  // null, so seed it from the link.
  const { selection, setSelection } = useAssistantUserSelection();
  const isLinkSeedPending = useSeedSelectionFromLink(setSelection);
  // Seeded from the URL on mount only; thereafter the source of truth is this
  // state, not `linkThreadId` (a thread jump updates both). Do not add an effect
  // syncing `selectedThreadId` back from `linkThreadId` — it would loop.
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(linkThreadId);
  const [deleteThreadConfirm, setDeleteThreadConfirm] = React.useState<ThreadRow | null>(null);
  const [deleteReason, setDeleteReason] = React.useState('');
  const [deleteAllOpen, setDeleteAllOpen] = React.useState(false);
  const [deleteAllReason, setDeleteAllReason] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  // Thread whose re-compaction is in flight. Latched on click so the link
  // becomes a non-interactive "processing…" label (prevents double-fire).
  // Backend is fire-and-forget with no completion signal, so it stays latched
  // until the operator switches threads (cleared in the detail-load effect) or
  // refreshes — the toast tells them to refresh to see the result.
  const [recompactingThreadId, setRecompactingThreadId] = React.useState<string | null>(null);
  // Thread whose memory re-extraction is in flight — same latch contract as
  // `recompactingThreadId`.
  const [reextractingThreadId, setReextractingThreadId] = React.useState<string | null>(null);
  // Latches once we auto-pick the first thread for a given user. Reset
  // on user switch so the next user gets their own auto-pick, but
  // unchanged on delete-then-refetch — operators who just confirmed a
  // delete shouldn't have a sibling thread jump into the detail pane
  // uninvited.
  // Skip the first-thread auto-pick when we arrived with a deep-linked thread —
  // the operator wants that exact thread, not threads[0].
  const hasAutoSelectedRef = React.useRef(linkThreadId !== null);
  // A thread opened by id alone — pasted into the jump box, or a `?threadId=`
  // deep link carrying no `userId` — arrives with no rail context, so the owner
  // is backfilled once from the loaded transcript. Latched rather than derived
  // because clicking a row in the fleet-wide rail must NOT backfill: that would
  // re-scope the rail to that row's owner and collapse the feed being browsed.
  const pendingOwnerBackfillRef = React.useRef(linkThreadId !== null && readLinkedUserId(searchParams) === null);

  const { showToast } = useToast();
  const { user: mopsUser } = useAuthState();

  const userId = selection?.userId ?? null;

  const [loadThreads, { data: threadsData, loading: threadsLoading, refetch: refetchThreads }] = useLazyQuery<
    AdminListRecentAssistantThreadsQuery,
    AdminListRecentAssistantThreadsQueryVariables
  >(ADMIN_LIST_THREADS, { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

  const [loadThreadDetail, { data: detailData, loading: detailLoading, fetchMore: fetchMoreMessages }] = useLazyQuery<
    AdminGetAssistantThreadDetailQuery,
    AdminGetAssistantThreadDetailQueryVariables
  >(ADMIN_GET_THREAD_DETAIL, { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

  const [loadFeedback, { data: feedbackData }] = useLazyQuery<
    AdminListAssistantFeedbackForThreadQuery,
    AdminListAssistantFeedbackForThreadQueryVariables
  >(ADMIN_LIST_FEEDBACK_FOR_THREAD, { fetchPolicy: 'cache-and-network' });

  const [deleteThread] = useMutation<AdminDeleteAssistantThreadMutation, AdminDeleteAssistantThreadMutationVariables>(
    ADMIN_DELETE_THREAD
  );

  const [deleteAllThreads] = useMutation<
    AdminDeleteAllAssistantThreadsForUserMutation,
    AdminDeleteAllAssistantThreadsForUserMutationVariables
  >(ADMIN_DELETE_ALL_THREADS);

  const [recompactThread] = useMutation<
    AdminRecompactAssistantThreadMutation,
    AdminRecompactAssistantThreadMutationVariables
  >(ADMIN_RECOMPACT_THREAD);

  const [reextractThread] = useMutation<
    AdminReExtractAssistantThreadMemoryMutation,
    AdminReExtractAssistantThreadMemoryMutationVariables
  >(ADMIN_REEXTRACT_THREAD);

  // Raw box contents vs. what actually reaches the server. Debounced so a
  // keystroke doesn't fire a sequential scan, and floored at the shared minimum
  // so a one-character query never leaves the rail looking empty.
  const [searchInput, setSearchInput] = React.useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();
  const activeSearch = trimmedSearch.length >= MIN_TRANSCRIPT_SEARCH_QUERY_LENGTH ? trimmedSearch : null;
  const searchTooShort = trimmedSearch.length > 0 && activeSearch === null;

  // No user selected is the fleet-wide feed, not a blocked query — an operator
  // arriving with nobody in mind still lands on real traffic. A `?userId=` link
  // holds the feed until its user is seeded; otherwise the auto-pick below can
  // open a fleet-wide thread belonging to someone else.
  React.useEffect(() => {
    if (!isLinkSeedPending) {
      void loadThreads({ variables: { userId, query: activeSearch } });
    }
  }, [userId, activeSearch, loadThreads, isLinkSeedPending]);

  // The transcript pane keeps its own scroll offset across a thread switch, so
  // without this an operator clicking through the rail lands part-way down each
  // new transcript at whatever depth they had reached in the last one. A
  // targeted turn scrolls itself into view afterwards, from the top.
  const transcriptPaneRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setRecompactingThreadId(null);
    setReextractingThreadId(null);
    if (transcriptPaneRef.current !== null) {
      transcriptPaneRef.current.scrollTop = 0;
    }
    if (selectedThreadId) {
      void loadThreadDetail({ variables: { threadId: selectedThreadId, afterSequence: null } });
      void loadFeedback({ variables: { threadId: selectedThreadId } });
    }
  }, [selectedThreadId, loadThreadDetail, loadFeedback]);

  // Select (or clear) the inspected thread and keep the URL's threadId in lockstep
  // so the address bar is always shareable/bookmarkable to the open transcript.
  // `replace` so clicking (or j/k-ing) through the thread list doesn't spam the
  // back stack with one entry per thread.
  const selectThread = React.useCallback(
    (threadId: string | null, turnId: string | null = null) => {
      // Every ordinary navigation lands here — a rail click, the prior-thread
      // link, the auto-pick, a user switch — and none of them wants the owner
      // backfill. Only the jump / deep-link path arms it, and it does so
      // without coming through here, so disarming unconditionally is safe.
      // Without this, a rail click made while a jump's detail request is still
      // in flight inherits the armed latch and re-scopes the rail to the
      // clicked row's owner once that request lands.
      pendingOwnerBackfillRef.current = false;
      setSelectedThreadId(threadId);
      setSearchParams((prev) => nextThreadSearchParams(prev, threadId, turnId), { replace: true });
    },
    [setSearchParams]
  );

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    selectThread(null);
    setDeleteThreadConfirm(null);
    setDeleteReason('');
    setDeleteAllOpen(false);
    setDeleteAllReason('');
    hasAutoSelectedRef.current = false;
  };

  // Jump straight to a transcript from the identifiers in a Sentry report. The
  // detail query needs only a thread id, so a paste of `threadId` (optionally
  // with the `turnId` to scroll to) loads the transcript without first picking
  // the user. Drives the URL so the result is shareable and the existing
  // `turnId` highlight plumbing fires.
  const [jumpInput, setJumpInput] = React.useState('');
  const handleThreadJump = () => {
    const jump = parseThreadJump(jumpInput);
    if (jump === null) {
      showToast({ type: 'error', message: 'Enter a valid thread id (UUID), optionally followed by a turn id' });
      return;
    }
    hasAutoSelectedRef.current = true;
    pendingOwnerBackfillRef.current = true;
    setSearchParams(jump.turnId ? { threadId: jump.threadId, turnId: jump.turnId } : { threadId: jump.threadId });
    setSelectedThreadId(jump.threadId);
    setJumpInput('');
  };

  const threads = React.useMemo(() => threadsData?.adminListRecentAssistantThreads?.threads ?? [], [threadsData]);
  const threadsHasMore = threadsData?.adminListRecentAssistantThreads?.hasMore ?? false;

  const { filteredThreads, channelCounts, activeChannel, setChannelFilter } = useThreadChannelFilter(threads, userId);
  const detail = detailData?.adminGetAssistantThreadDetail ?? null;

  // Feedback for the selected thread, grouped by the turn it targets. Filtered
  // to the active thread so a stale response from a just-switched thread can't
  // bleed badges onto the wrong turns.
  const feedbackByTurnId = React.useMemo(() => {
    const map = new Map<string, FeedbackRow[]>();
    for (const fb of feedbackData?.adminListAssistantFeedbackForThread ?? []) {
      if (fb.threadId !== selectedThreadId) {
        continue;
      }
      const list = map.get(fb.turnId) ?? [];
      list.push(fb);
      map.set(fb.turnId, list);
    }
    return map;
  }, [feedbackData, selectedThreadId]);

  // Auto-pick the first thread once per user selection so the operator
  // lands on real data instead of a blank pane. The ref guard prevents
  // a post-delete refetch from quietly jumping the operator to a sibling
  // thread — `selectedThreadId === null` after a delete looks identical
  // to "nothing loaded yet" without it.
  // The `userId` match guards a switch-users race: the lazy query holds the
  // prior user's `threads` until the refetch resolves, so auto-picking one of
  // theirs would open a transcript the rail is about to stop showing. The
  // fleet-wide feed has no such race — every row belongs on it.
  React.useEffect(() => {
    if (
      !hasAutoSelectedRef.current &&
      selectedThreadId === null &&
      threads.length > 0 &&
      (userId === null || threads[0].userId === userId)
    ) {
      hasAutoSelectedRef.current = true;
      selectThread(threads[0].id);
    }
  }, [threads, selectedThreadId, selectThread, userId]);

  // Point the user selection at the owner of a transcript opened by id alone,
  // so the rail and the bulk-delete stop describing whoever was selected
  // before. Only fires for the jump / deep-link path (see
  // `pendingOwnerBackfillRef`), and only once the loaded detail is actually the
  // requested thread — `useLazyQuery` keeps the previous thread's `detail`
  // populated across the switch, and backfilling off that would adopt the wrong
  // owner.
  React.useEffect(() => {
    if (!pendingOwnerBackfillRef.current || selectedThreadId === null || detail?.thread?.id !== selectedThreadId) {
      return;
    }
    pendingOwnerBackfillRef.current = false;
    if (detail.thread.userId !== selection?.userId) {
      setSelection({
        userId: detail.thread.userId,
        familyId: detail.thread.familyId,
        // Carried so the bulk-delete confirmation can name the user it is about
        // to wipe, rather than falling back to a raw id on this path.
        email: detail.thread.userEmail ?? undefined,
      });
    }
  }, [selection, selectedThreadId, detail, setSelection]);

  // Resolver paginates forward in `sequence ASC` (the orchestrator's
  // reconnect-replay shape); each call adds messages that came after the
  // ones we already have, so the affordance reads "Load more" rather
  // than "older" / "newer" to stay direction-honest. Whether the pane
  // should flip to chat-style "newest first" UX is tracked separately.
  const handleLoadMoreMessages = async () => {
    // `detail` is the last *resolved* thread, so between selecting a thread and
    // its request landing it still describes the previous one. Paging on that
    // would fetch the old thread's next page and merge it into the pane the new
    // thread is about to fill.
    if (!detail || detail.thread.id !== selectedThreadId) {
      return;
    }
    if (!detail.hasMore || detail.nextAfterSequence === null || detail.nextAfterSequence === undefined) {
      return;
    }
    await fetchMoreMessages({
      variables: { threadId: detail.thread.id, afterSequence: detail.nextAfterSequence },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          adminGetAssistantThreadDetail: {
            ...fetchMoreResult.adminGetAssistantThreadDetail,
            messages: [
              ...prev.adminGetAssistantThreadDetail.messages,
              ...fetchMoreResult.adminGetAssistantThreadDetail.messages,
            ],
          },
        };
      },
    });
  };

  // A targeted turn — a search hit, or a `?turnId=` deep link from Sentry — can
  // sit anywhere in a thread of up to DEFAULT_MAX_THREAD_MESSAGES rows, while
  // the detail query pages forward from the start ADMIN_MESSAGE_PAGE_SIZE at a
  // time. Land on a turn past the first page and it is simply not in the DOM:
  // nothing scrolls, nothing highlights, and the failure is silent because the
  // rail still reported a hit. So keep pulling pages until the turn arrives or
  // the thread runs out. The ref keeps one fetch in flight at a time; each
  // append re-runs this effect, which walks the thread a page per pass and
  // stops on the `hasMore` guard inside `handleLoadMoreMessages`.
  const seekingTurnRef = React.useRef(false);

  // Switching threads abandons a seek in flight. The latch is per-thread, and
  // an armed one left over from the previous thread would suppress the new
  // thread's seek until the old fetch happened to settle. Declared before the
  // seek effect so it disarms first on the render that changes the selection.
  React.useEffect(() => {
    seekingTurnRef.current = false;
  }, [selectedThreadId]);

  React.useEffect(() => {
    if (highlightTurnId === null || detail === null || seekingTurnRef.current) {
      return;
    }
    // `detail` trails the selection while the new thread's request is in
    // flight; seeking against the previous thread's messages asks the wrong
    // thread for the turn and never finds it.
    if (detail.thread.id !== selectedThreadId) {
      return;
    }
    if (detail.messages.some((m) => m.turnId === highlightTurnId)) {
      return;
    }
    seekingTurnRef.current = true;
    void handleLoadMoreMessages().finally(() => {
      seekingTurnRef.current = false;
    });
    // `handleLoadMoreMessages` is re-created every render; depending on it would
    // re-fire this effect continuously. The state it reads (`detail`) is a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTurnId, detail, selectedThreadId]);

  const handleConfirmDeleteThread = async () => {
    if (!deleteThreadConfirm || deleteReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      await deleteThread({
        variables: {
          threadId: deleteThreadConfirm.id,
          reason: prefixReason(deleteReason, mopsUser?.email ?? null),
        },
      });
      showToast({ type: 'success', message: 'Thread deleted.' });
      const wasSelected = selectedThreadId === deleteThreadConfirm.id;
      setDeleteThreadConfirm(null);
      setDeleteReason('');
      if (wasSelected) {
        selectThread(null);
      }
      await refetchThreads();
    } catch {
      // Apollo errorLink reports to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  // Force a fresh compaction run on an archived thread so we can eyeball the
  // result on real data. Fire-and-forget on the backend (the `@orn/api` worker
  // returns 202 and runs the LLM call in the background), so there's no
  // deterministic completion signal — the toast tells the operator to refresh.
  const handleRecompactThread = async (threadId: string) => {
    setRecompactingThreadId(threadId);
    try {
      await recompactThread({ variables: { threadId } });
      showToast({
        type: 'info',
        message: 'Re-compaction queued — it will finish shortly. Refresh in a few seconds to see the result.',
      });
      // Stays latched as "processing…" — there's no completion signal for the
      // background compaction; switching threads or refreshing clears it.
    } catch {
      // Apollo errorLink reports to Sentry; a 404/409 from the worker surfaces
      // as a GraphQL error here. Unlatch so the operator can retry.
      setRecompactingThreadId(null);
    }
  };

  // Re-run memory extraction on an archived thread: the worker deletes the
  // thread's prior extractor memories and re-mines them. Same fire-and-forget
  // latch contract as re-compaction. A 409 (non-archived) surfaces as a
  // GraphQL error → caught here, unlatched.
  const handleReextractThread = async (threadId: string) => {
    setReextractingThreadId(threadId);
    try {
      await reextractThread({ variables: { threadId } });
      showToast({
        type: 'info',
        message: 'Re-extraction queued — it will finish shortly. Refresh in a few seconds to see the result.',
      });
    } catch {
      setReextractingThreadId(null);
    }
  };

  const handleConfirmDeleteAll = async () => {
    if (!userId || deleteAllReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      const result = await deleteAllThreads({
        variables: { userId, reason: prefixReason(deleteAllReason, mopsUser?.email ?? null) },
      });
      const count = result.data?.adminDeleteAllAssistantThreadsForUser ?? 0;
      showToast({
        type: 'success',
        message: count === 0 ? 'No threads to delete.' : `Deleted ${count} threads.`,
      });
      setDeleteAllOpen(false);
      setDeleteAllReason('');
      selectThread(null);
      await refetchThreads();
    } catch {
      // Apollo errorLink reports to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  // Which of the three ways the rail can come up empty the operator is looking
  // at — "nothing matched" and "this user has none" are different problems.
  const emptyRailMessage =
    activeSearch !== null
      ? `No transcripts contain “${activeSearch}”${userId ? ' for this user' : ''}.`
      : userId
        ? 'No assistant threads for this user.'
        : 'No assistant threads on record.';

  // The line under the search box, in the order the operator hits the states:
  // too short to run, then not searching at all (no line), then in flight, then
  // the result. The short-query case leads because it is the only one that has
  // to override a stale count still sitting on screen from the previous query.
  const scopeLabel = userId ? 'this user' : 'all users';
  const searchStatus = searchTooShort
    ? `Type at least ${MIN_TRANSCRIPT_SEARCH_QUERY_LENGTH} characters to search.`
    : activeSearch === null
      ? null
      : threadsLoading
        ? `Searching ${scopeLabel}…`
        : `${threads.length}${threadsHasMore ? '+' : ''} matching thread${threads.length === 1 ? '' : 's'} across ${scopeLabel}.`;

  return (
    <>
      {/* Both fields go label-less: each placeholder already says what the field
          takes, and the headings cost a row of vertical space the transcript
          wants. The names survive as `aria-label`s. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-96 shrink-0">
            <AdminSelectUser
              selectedUserId={selection?.userId ?? null}
              onSelectionChange={handleUserSelect}
              placeholder="Filter to one user — name or email…"
              hideLabel
            />
          </div>
          <form
            className="flex shrink-0 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleThreadJump();
            }}
          >
            <Input
              id="jump-thread-input"
              aria-label="Jump to thread"
              className="w-96"
              value={jumpInput}
              onChange={setJumpInput}
              placeholder="Jump to thread id (+ optional turn id)"
              inputClassName="py-2.5 font-mono"
            />
            <Button type="submit" color="secondary" size="sm" isDisabled={jumpInput.trim().length === 0}>
              Open
            </Button>
          </form>
        </div>
        {userId && (
          <Button color="danger" size="sm" isDisabled={actionLoading} onClick={() => setDeleteAllOpen(true)}>
            Delete all threads
          </Button>
        )}
      </div>

      {threads.length > 0 && (
        <ThreadChannelFilterBar
          counts={channelCounts}
          activeChannel={activeChannel}
          totalCount={threads.length}
          truncated={threadsHasMore}
          onSelect={setChannelFilter}
        />
      )}

      <div className="border-border-primary flex min-h-0 flex-1 border">
        {/* The search box is part of the rail's chrome, not of the list, so it
            survives a query that matches nothing — otherwise the control that
            produced the empty state would unmount with it and leave no way to
            edit the term. */}
        <aside className="border-border-primary flex w-80 shrink-0 flex-col border-r">
          <div className="border-border-primary shrink-0 border-b p-2">
            <Input
              id="search-transcripts-input"
              icon={SmallSearchIcon}
              aria-label="Search transcripts"
              value={searchInput}
              onChange={setSearchInput}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchInput('');
                }
              }}
              placeholder="Search transcripts…"
              inputClassName="py-1.5 text-xs"
            />
            {searchStatus !== null && <div className="text-fg-tertiary mt-1.5 text-[10px]">{searchStatus}</div>}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {threadsLoading && threads.length === 0 ? (
              <div className="text-fg-secondary p-3 text-xs">Loading threads…</div>
            ) : threads.length === 0 ? (
              <div className="text-fg-tertiary p-3 text-xs italic">{emptyRailMessage}</div>
            ) : (
              <ThreadList
                threads={filteredThreads}
                selectedThreadId={selectedThreadId}
                onSelect={selectThread}
                hasMore={threadsHasMore}
              />
            )}
          </div>
        </aside>
        <div ref={transcriptPaneRef} className="flex-1 overflow-y-auto">
          {selectedThreadId === null ? (
            <div className="text-fg-tertiary p-6 text-xs italic">Pick a thread from the left to inspect it.</div>
          ) : detailLoading && detail === null ? (
            <div className="text-fg-secondary p-6 text-xs">Loading messages…</div>
          ) : detail === null ? (
            <div className="text-fg-tertiary p-6 text-xs italic">Thread detail unavailable.</div>
          ) : (
            <ThreadDetail
              detail={detail}
              actionLoading={actionLoading}
              onDelete={() => {
                setDeleteThreadConfirm(detail.thread);
                setDeleteReason('');
              }}
              onRecompact={() => void handleRecompactThread(detail.thread.id)}
              isRecompacting={recompactingThreadId === detail.thread.id}
              onReextract={() => void handleReextractThread(detail.thread.id)}
              isReextracting={reextractingThreadId === detail.thread.id}
              onLoadMore={() => void handleLoadMoreMessages()}
              onJumpToPrior={(priorId) => {
                if (threads.some((s) => s.id === priorId)) {
                  selectThread(priorId);
                }
              }}
              threadsInList={threads}
              feedbackByTurnId={feedbackByTurnId}
              highlightTurnId={highlightTurnId}
              highlightQuery={activeSearch}
            />
          )}
        </div>
      </div>

      <Modal
        isOpen={deleteThreadConfirm !== null}
        onOpenChange={(open) => !open && setDeleteThreadConfirm(null)}
        size="md"
      >
        <Modal.Header onClose={() => setDeleteThreadConfirm(null)}>Delete thread</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              Hard-deletes this thread and all its messages — irreversible. If the user has an active WebSocket
              conversation in this thread right now, the in-flight turn may be lost. Reason is recorded in operational
              logs.
            </div>
            {deleteThreadConfirm && (
              <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
                <div className="font-mono text-[11px]">
                  {deleteThreadConfirm.id} · {deleteThreadConfirm.messageCount} msg · {deleteThreadConfirm.channel} ·{' '}
                  {deleteThreadConfirm.status}
                </div>
              </div>
            )}
            <Input
              label="Reason"
              placeholder="e.g. user privacy request, support ticket #1234"
              value={deleteReason}
              onChange={setDeleteReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setDeleteThreadConfirm(null)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={deleteReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleConfirmDeleteThread()}
              >
                Delete thread
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal isOpen={deleteAllOpen} onOpenChange={(open) => !open && setDeleteAllOpen(false)} size="md">
        <Modal.Header onClose={() => setDeleteAllOpen(false)}>Delete all threads for user</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              Hard-deletes <span className="font-semibold">every</span> assistant thread (active and archived) and all
              messages for this user — irreversible. If the user has an active WebSocket conversation, the in-flight
              turn may be lost. Reason is recorded in operational logs.
            </div>
            {/* Names the target rather than counting the rail: a channel filter
                or an active search means the rail is a subset, and a count that
                undersells what a hard delete is about to take is worse than no
                count. The toast reports the real number afterwards. */}
            <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
              <span className="font-mono text-[11px]">{selection?.email ?? userId}</span>
            </div>
            <Input
              label="Reason"
              placeholder="e.g. account deletion / GDPR request"
              value={deleteAllReason}
              onChange={setDeleteAllReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setDeleteAllOpen(false)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={deleteAllReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleConfirmDeleteAll()}
              >
                Delete all
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

interface IThreadListProps {
  threads: ThreadRow[];
  selectedThreadId: string | null;
  /** `turnId` is the search hit's turn when the row carries one, so the transcript opens scrolled to it. */
  onSelect: (threadId: string, turnId?: string | null) => void;
  hasMore: boolean;
}

/**
 * The thread list inside the rail. Every row leads with when the thread was last
 * active and who owns it — the owner label carries the fleet-wide feed, where
 * consecutive rows routinely belong to different people. On a search, the row
 * reports its hit count and opens the transcript at the matching turn; the match
 * itself is shown highlighted in the transcript rather than previewed here,
 * where there is no room for it.
 */
const ThreadList = ({ threads, selectedThreadId, onSelect, hasMore }: IThreadListProps) => (
  <>
    {hasMore && (
      <div className="border-border-primary text-fg-warning-primary border-b px-3 py-2 text-[11px]">
        Showing 100 most recent · older threads hidden
      </div>
    )}
    <ul>
      {threads.map((thread) => {
        const isSelected = thread.id === selectedThreadId;
        const match = thread.searchMatch;
        return (
          <li key={thread.id}>
            <button
              type="button"
              onClick={() => onSelect(thread.id, match?.turnId ?? null)}
              className={cx(
                'border-border-primary flex w-full flex-col gap-1 border-b px-3 py-2 text-left transition-colors',
                isSelected
                  ? 'bg-bg-secondary text-fg-primary border-l-fg-brand-primary border-l-2 pl-[10px]'
                  : 'hover:bg-bg-secondary text-fg-secondary'
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="shrink-0 text-[11px] font-medium">{formatTimeAgo(thread.lastActivityAt)}</span>
                {thread.userEmail !== null && thread.userEmail !== undefined && (
                  <span
                    className="text-fg-tertiary min-w-0 flex-1 truncate font-mono text-[10px]"
                    title={thread.userEmail}
                  >
                    {ellipsizeEmail(thread.userEmail, RAIL_EMAIL_MAX_CHARS)}
                  </span>
                )}
                <span className="text-fg-quaternary ml-auto shrink-0 font-mono text-[10px]">{thread.id.slice(-8)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={cx(
                    'inline-flex items-center border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider',
                    CHANNEL_BADGE[thread.channel] ?? CHANNEL_BADGE.app
                  )}
                >
                  {thread.channel}
                </span>
                {thread.latestDirective !== null && thread.latestDirective !== undefined && (
                  <span className="border-border-secondary text-fg-tertiary inline-flex min-w-0 items-center border border-dashed px-1.5 py-px text-[9px] font-medium uppercase tracking-wider">
                    <span className="truncate">{thread.latestDirective.replace(/_/g, ' ')}</span>
                  </span>
                )}
                <span
                  className={cx(
                    'inline-flex items-center border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider',
                    STATUS_BADGE[thread.status] ?? STATUS_BADGE.active
                  )}
                >
                  {thread.status}
                </span>
                <span className="text-fg-tertiary font-mono text-[10px]">{thread.messageCount} msg</span>
                {match && (
                  <span className="text-fg-brand-primary font-mono text-[10px]">
                    {match.matchCount} hit{match.matchCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  </>
);

type ThreadDetailRow = NonNullable<AdminGetAssistantThreadDetailQuery['adminGetAssistantThreadDetail']>['thread'];

interface IUsageHeaderRowsProps {
  thread: ThreadDetailRow;
}

/**
 * Model spend for the whole thread as `<dl>` rows for the header — same
 * bare-`<dt>`/`<dd>` contract as {@link CompactionHeaderRows}. The sums cover
 * every persisted round, not the loaded page, so they don't climb as the
 * operator pages back through the transcript.
 *
 * Labeled "billed", because it is what the thread charged rather than the size
 * of the conversation: each round re-sends the whole prompt and pays for it
 * again, so the totals run far above the final context. The breakdown says
 * where it went — a thread that looks expensive on `tokens` is usually cheap
 * because almost all of it was cache reads.
 */
const UsageHeaderRows = ({ thread }: IUsageHeaderRowsProps) => {
  const usage = thread.usage;
  if (usage === null || usage === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">billed</dt>
        <dd className="text-fg-tertiary italic">no usage recorded</dd>
      </>
    );
  }
  return (
    <>
      <dt
        className="text-fg-tertiary"
        title="Summed cost of every round the thread ran, priced by the provider at call time."
      >
        billed
      </dt>
      <dd className="text-fg-primary font-mono tabular-nums">
        {formatCostUsd(usage.costUsd)} over {usage.roundCount} rounds
      </dd>
      <dt
        className="text-fg-tertiary pl-3"
        title="Summed across rounds. The whole prompt is re-sent every round, so this runs well above the thread's final context size."
      >
        tokens
      </dt>
      <dd className="font-mono tabular-nums">{formatTokens(usage.totalTokens)}</dd>
      <dt
        className="text-fg-tertiary pl-3"
        title="Uncached input / cache read / cache write. Cache reads bill well below uncached input."
      >
        in/read/write
      </dt>
      <dd className="font-mono tabular-nums">
        {formatTokens(usage.inputTokens)} / {formatTokens(usage.cacheReadTokens)} /{' '}
        {formatTokens(usage.cacheWriteTokens)}
      </dd>
      <dt
        className="text-fg-tertiary pl-3"
        title="Generated tokens, and the thinking subset of them (reasoning is included in output, not additional to it)."
      >
        out/think
      </dt>
      <dd className="font-mono tabular-nums">
        {formatTokens(usage.outputTokens)} / {formatTokens(usage.reasoningTokens)}
      </dd>
    </>
  );
};

interface ICompactionHeaderRowsProps {
  thread: ThreadDetailRow;
}

/**
 * Compaction stats as `<dl>` rows for the thread header. Renders inside
 * the parent's grid (so it must emit bare `<dt>`/`<dd>` pairs, not a
 * wrapper). Shows the per-run counts + token spend once `compactedAt` is
 * set; a single "not compacted" row until then.
 */
const CompactionHeaderRows = ({ thread }: ICompactionHeaderRowsProps) => {
  if (thread.compactedAt === null || thread.compactedAt === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">compaction</dt>
        <dd className="text-fg-tertiary italic">not compacted</dd>
      </>
    );
  }
  const m = thread.compactionMetadata;
  return (
    <>
      <dt className="text-fg-tertiary">compacted</dt>
      <dd className="font-mono">{formatTimeAgo(thread.compactedAt)}</dd>
      <dt className="text-fg-tertiary pl-3">model</dt>
      <dd className="font-mono">{thread.compactionModelId ?? '—'}</dd>
      {m !== null && m !== undefined && (
        <>
          <dt className="text-fg-tertiary pl-3" title="kept / summarized / dropped messages">
            kept/summ/drop
          </dt>
          <dd className="font-mono tabular-nums">
            {m.keptCount} / {m.summarizedCount} / {m.droppedCount}
          </dd>
          <dt className="text-fg-tertiary pl-3">tokens</dt>
          <dd className="font-mono tabular-nums">
            {m.inputTokens} in / {m.outputTokens} out
          </dd>
          {m.errors !== null && m.errors !== undefined && m.errors.length > 0 && (
            <>
              <dt className="text-fg-tertiary pl-3">errors</dt>
              <dd className="text-fg-error-primary font-mono tabular-nums" title={m.errors.join('\n')}>
                {m.errors.length}
              </dd>
            </>
          )}
        </>
      )}
    </>
  );
};

/**
 * Memory-extraction stats as `<dl>` rows for the thread header — the backup
 * pass that mines durable memories the agent's in-thread tool missed. Same
 * bare-`<dt>`/`<dd>` contract as {@link CompactionHeaderRows}; a single "not
 * extracted" row until `memoryExtractedAt` is set.
 */
const MemoryExtractionHeaderRows = ({ thread }: ICompactionHeaderRowsProps) => {
  if (thread.memoryExtractedAt === null || thread.memoryExtractedAt === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">extraction</dt>
        <dd className="text-fg-tertiary italic">not extracted</dd>
      </>
    );
  }
  const m = thread.memoryExtractionMetadata;
  return (
    <>
      <dt className="text-fg-tertiary">extracted</dt>
      <dd className="font-mono">{formatTimeAgo(thread.memoryExtractedAt)}</dd>
      <dt className="text-fg-tertiary pl-3">model</dt>
      <dd className="font-mono">{thread.memoryExtractionModelId ?? '—'}</dd>
      {m !== null && m !== undefined && (
        <>
          <dt className="text-fg-tertiary pl-3" title="total mined (user / family · pref)">
            mined
          </dt>
          <dd className="font-mono tabular-nums">
            {m.extractedCount} ({m.userCount} usr / {m.familyCount} fam · {m.prefCount} pref)
          </dd>
          <dt className="text-fg-tertiary pl-3">tokens</dt>
          <dd className="font-mono tabular-nums">
            {m.inputTokens} in / {m.outputTokens} out
          </dd>
          {m.updatedCount !== null && m.updatedCount !== undefined && (
            <>
              <dt
                className="text-fg-tertiary pl-3"
                title="rewritten / forgotten / skipped stale / dropped as already stored · memories shown (+ = window cut)"
              >
                reconciled
              </dt>
              <dd className="font-mono tabular-nums">
                {m.updatedCount} upd / {m.forgottenCount ?? 0} fgt / {m.staleSkipCount ?? 0} stale /{' '}
                {m.duplicateSkipCount ?? 0} dup · {m.existingMemoryCount ?? 0}
                {m.existingTruncated === true ? '+' : ''} shown
                {m.capExceeded === true && (
                  <span className="text-fg-error-primary"> · cap exceeded, updates/forgets dropped</span>
                )}
              </dd>
            </>
          )}
          {m.errors !== null && m.errors !== undefined && m.errors.length > 0 && (
            <>
              <dt className="text-fg-tertiary pl-3">errors</dt>
              <dd className="text-fg-error-primary font-mono tabular-nums" title={m.errors.join('\n')}>
                {m.errors.length}
              </dd>
            </>
          )}
        </>
      )}
    </>
  );
};

interface ICompactionSummaryPanelProps {
  summary: string;
}

/**
 * The compaction summary, collapsed to a one-line preview that expands to
 * the full narrative on click. Mirrors `CollapsibleMessageCard`'s header
 * affordance (chevron + uppercase label + truncated preview). Only
 * rendered when the thread carries a non-empty summary.
 */
const CompactionSummaryPanel = ({ summary }: ICompactionSummaryPanelProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const preview = firstLineOfSummary(summary);
  return (
    <div className="border-border-primary border-b pb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="hover:bg-bg-secondary -ml-1 flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] uppercase"
      >
        <span className="text-fg-quaternary shrink-0">{expanded ? '▾' : '▸'}</span>
        <span className="text-fg-brand-primary shrink-0">summary</span>
        {!expanded && <span className="text-fg-secondary min-w-0 flex-1 truncate normal-case">{preview}</span>}
      </button>
      {expanded && (
        <div className="text-fg-primary whitespace-pre-wrap px-1 pt-2 font-mono text-[11px] leading-snug">
          <InspectLinkedText text={summary} />
        </div>
      )}
    </div>
  );
};

interface IThreadDetailProps {
  detail: NonNullable<AdminGetAssistantThreadDetailQuery['adminGetAssistantThreadDetail']>;
  actionLoading: boolean;
  onDelete: () => void;
  onRecompact: () => void;
  isRecompacting: boolean;
  onReextract: () => void;
  isReextracting: boolean;
  onLoadMore: () => void;
  onJumpToPrior: (priorThreadId: string) => void;
  threadsInList: ThreadRow[];
  feedbackByTurnId: Map<string, FeedbackRow[]>;
  /** turnId to scroll to + highlight when arriving from a Feedback-tab deep link. */
  highlightTurnId: string | null;
  /** Active transcript-search term; occurrences are marked throughout the transcript. */
  highlightQuery: string | null;
}

const ThreadDetail = ({
  detail,
  actionLoading,
  onDelete,
  onRecompact,
  isRecompacting,
  onReextract,
  isReextracting,
  onLoadMore,
  onJumpToPrior,
  threadsInList,
  feedbackByTurnId,
  highlightTurnId,
  highlightQuery,
}: IThreadDetailProps) => {
  const { thread, messages, hasMore } = detail;
  const priorIsInList = thread.priorThreadId !== null && threadsInList.some((s) => s.id === thread.priorThreadId);
  // Over ALL loaded messages, not per turn: a notify can land turns after the
  // creates it covers, and "Load more" appends pages that can complete a pair.
  const alertNotifications = React.useMemo(() => buildAlertNotificationIndex(messages), [messages]);
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="border-border-primary flex items-start justify-between gap-3 border-b pb-3">
        <dl className="text-fg-secondary grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          <dt className="text-fg-tertiary">id</dt>
          <dd className="font-mono">
            <InspectLink id={thread.id} />
          </dd>
          {/* The rail can be showing every user's threads, so the transcript has
              to say whose it is rather than leaving the picker to imply it. */}
          <dt className="text-fg-tertiary">owner</dt>
          <dd className="font-mono" title={thread.userId}>
            <InspectLinkedText text={thread.userEmail ?? thread.userId} />
          </dd>
          <dt className="text-fg-tertiary">channel</dt>
          <dd>{thread.channel}</dd>
          <dt className="text-fg-tertiary">status</dt>
          <dd>{thread.status}</dd>
          <dt className="text-fg-tertiary">messages</dt>
          <dd>{thread.messageCount}</dd>
          <UsageHeaderRows thread={thread} />
          <dt className="text-fg-tertiary">created</dt>
          <dd className="font-mono">{formatInstantInTimeZone(thread.createdAt, PACIFIC_TIME_ZONE)}</dd>
          <dt className="text-fg-tertiary">last activity</dt>
          <dd className="font-mono">{formatTimeAgo(thread.lastActivityAt)}</dd>
          {thread.priorThreadId && (
            <>
              <dt className="text-fg-tertiary">prior</dt>
              <dd className="font-mono">
                {priorIsInList ? (
                  <button
                    type="button"
                    onClick={() => thread.priorThreadId && onJumpToPrior(thread.priorThreadId)}
                    className="text-fg-brand-primary hover:underline"
                  >
                    {thread.priorThreadId.slice(-8)}
                  </button>
                ) : (
                  <span className="text-fg-tertiary" title={thread.priorThreadId}>
                    {thread.priorThreadId.slice(-8)} (not loaded)
                  </span>
                )}
              </dd>
            </>
          )}
          <CompactionHeaderRows thread={thread} />
          <dt className="text-fg-tertiary pl-3">re-compact</dt>
          <dd>
            {isRecompacting ? (
              <span className="text-fg-quaternary italic">processing…</span>
            ) : (
              <button
                type="button"
                onClick={onRecompact}
                disabled={actionLoading || thread.status !== 'archived'}
                title={thread.status === 'archived' ? undefined : 'Only archived threads can be re-compacted'}
                className="text-fg-brand-primary disabled:text-fg-quaternary hover:underline disabled:cursor-not-allowed disabled:no-underline"
              >
                re-compact now
              </button>
            )}
          </dd>
          <MemoryExtractionHeaderRows thread={thread} />
          <dt className="text-fg-tertiary pl-3">re-extract</dt>
          <dd>
            {isReextracting ? (
              <span className="text-fg-quaternary italic">processing…</span>
            ) : (
              <button
                type="button"
                onClick={onReextract}
                disabled={actionLoading || thread.status !== 'archived'}
                title={thread.status === 'archived' ? undefined : 'Only archived threads can be re-extracted'}
                className="text-fg-brand-primary disabled:text-fg-quaternary hover:underline disabled:cursor-not-allowed disabled:no-underline"
              >
                re-extract now
              </button>
            )}
          </dd>
        </dl>
        <Button color="danger" size="sm" isDisabled={actionLoading} onClick={onDelete}>
          Delete thread
        </Button>
      </div>

      {thread.summary !== null && thread.summary !== undefined && thread.summary.length > 0 && (
        <CompactionSummaryPanel summary={thread.summary} />
      )}

      {messages.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No messages persisted for this thread.</div>
      ) : (
        <div className="flex flex-col">
          {groupMessagesByTurn(messages).map((group) => (
            <React.Fragment key={group.turnId}>
              {isResumeInterruptedTurn(group) && <ResumeInterstitial />}
              <TurnGroup
                group={group}
                feedback={feedbackByTurnId.get(group.turnId) ?? EMPTY_FEEDBACK}
                highlight={highlightTurnId === group.turnId}
                highlightQuery={highlightQuery}
                alertNotifications={alertNotifications}
              />
            </React.Fragment>
          ))}
          {hasMore && (
            <div className="flex justify-center py-3">
              <Button color="secondary" size="sm" isDisabled={actionLoading} onClick={onLoadMore}>
                Load more messages
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantThreadsScene;
